import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin } from "lucide-react";
import { scrollToSection } from "@/lib/lenis";
import { ADDRESS } from "@/lib/constants";
import { statusLabel } from "@/lib/status";
import { useLang } from "@/lib/i18n";
import vetrina from "@/assets/vetrina.jpg";

const lineVariants = {
  hidden: { y: "112%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.2 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ status }) {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="grain relative min-h-screen overflow-hidden bg-night">
      <div className="spotlight pointer-events-none absolute -right-40 top-10 h-[42rem] w-[42rem]" />
      <motion.div style={{ opacity: fade }} className="relative mx-auto max-w-7xl px-5 pt-32 sm:px-8 lg:pt-40">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {status && (
              <motion.div
                data-testid="hero-aperto-status-badge"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.15, duration: 0.6 }}
                className="mb-8 inline-flex items-center gap-2.5 rounded-full border border-ivory/20 bg-white/5 px-4 py-2 backdrop-blur"
              >
                <span
                  className={`h-2 w-2 rounded-full ${status.open ? "bg-emerald-400 animate-pulse" : "bg-bordeaux-light"}`}
                />
                <span className="font-mono-alt text-[11px] uppercase tracking-[0.22em] text-ivory">
                  {statusLabel(t, status)}
                </span>
              </motion.div>
            )}

            <h1 className="font-display text-6xl font-medium leading-[0.96] tracking-tight text-[#FDFBF7] sm:text-7xl lg:text-[6.8rem]">
              <span className="block overflow-hidden pb-1">
                <motion.span custom={0} initial="hidden" animate="visible" variants={lineVariants} className="block">
                  {t.hero.line1}
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-2">
                <motion.span custom={1} initial="hidden" animate="visible" variants={lineVariants} className="block">
                  {t.hero.line2a}
                  <span className="italic text-ivory">{t.hero.line2em}</span>
                  {t.hero.line2b}
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.7 }}
              className="mt-7 max-w-md text-base leading-relaxed text-ivory/70 sm:text-lg"
            >
              {t.hero.para}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.7 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <button
                data-testid="hero-cta-gusti"
                onClick={() => scrollToSection("gusti")}
                className="group rounded-full bg-bordeaux px-8 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-ivory transition-transform duration-300 hover:scale-[1.04]"
              >
                {t.hero.cta1}
              </button>
              <button
                data-testid="hero-cta-contatti"
                onClick={() => scrollToSection("contatti")}
                className="inline-flex items-center gap-2 rounded-full border border-ivory/30 px-8 py-4 font-mono-alt text-xs uppercase tracking-[0.22em] text-ivory transition-colors duration-300 hover:bg-ivory/10"
              >
                <MapPin className="h-4 w-4" /> {t.hero.cta2}
              </button>
            </motion.div>
          </div>

          <div className="relative lg:col-span-5">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="arch-frame relative mx-auto aspect-[3/4] max-w-sm overflow-hidden border border-ivory/15 shadow-[0_40px_120px_-30px_rgba(171,24,43,0.55)]"
            >
              <motion.img
                src={vetrina}
                alt="La vetrina del gelato artigianale Imperiale"
                style={{ y: imgY, scale: 1.18 }}
                className="h-full w-full object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6 }}
              className="absolute -bottom-4 left-1/2 w-max -translate-x-1/2 rounded-2xl border border-ivory/15 bg-night/80 px-5 py-3 backdrop-blur-md"
            >
              <p className="font-display italic text-lg text-ivory">{t.hero.badgeFresh}</p>
              <p className="font-mono-alt text-[10px] uppercase tracking-[0.25em] text-ivory/50">{ADDRESS}</p>
            </motion.div>
          </div>
        </div>

        <motion.button
          data-testid="hero-scroll-prompt"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6 }}
          onClick={() => scrollToSection("storia")}
          className="mx-auto mt-20 flex flex-col items-center gap-2 pb-10 text-ivory/50 transition-colors hover:text-ivory"
        >
          <span className="font-mono-alt text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
          <motion.span animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 1.8 }}>
            <ArrowDown className="h-4 w-4" />
          </motion.span>
        </motion.button>
      </motion.div>
    </section>
  );
}
